import { jsxs, jsx } from "react/jsx-runtime";
import React, { useState, useMemo, useEffect, useContext } from "react";
import ModalLib from "react-responsive-modal";
import { IconCancel, IconArrowLeft } from "@bynder/icons";
import { Flex } from "./Flex.js";
import { Button } from "./Button.js";
import { Sidebar } from "./Sidebar.js";
import { CloseButton, GlobalStyles, HeaderStyled, BackButton, Headings, Title, SecondaryInformation, AdditionalActions, ContentStyled, ContentInner, ContentExpander, FooterStyled, ActionsRight, SidebarStyled, classNames } from "./ModalBase.styles.js";
import { useZIndex } from "./useZIndex.js";
import { useTranslate } from "./useTranslate.js";
const ModalContext = React.createContext({
  hideCloseIcon: false,
  setHasSidebar: () => {
  }
  // eslint-disable-line @typescript-eslint/no-empty-function
});
const modalSizes = {
  small: classNames.modalSmall,
  medium: classNames.modalMedium,
  fullscreen: classNames.modalFullScreen
};
function ModalBase({
  isOpen,
  onClose,
  size = "small",
  isCloseButtonHidden = false,
  isClosedOnOverlayClick = false,
  onCloseAnimationEnd,
  children,
  container,
  ...props
}) {
  const [hasSidebar, setHasSidebar] = useState(false);
  const modalClassNames = {
    root: classNames.root,
    overlay: classNames.overlay,
    modalContainer: classNames.modalContainer,
    modal: `${classNames.modal} ${modalSizes[size]} ${hasSidebar ? classNames.modalHasSidebar : ""}`,
    modalAnimationIn: classNames.modalAnimationIn,
    modalAnimationOut: classNames.modalAnimationOut,
    overlayAnimationIn: classNames.overlayAnimationIn,
    overlayAnimationOut: classNames.overlayAnimationOut
  };
  const {
    translate
  } = useTranslate();
  const showCloseButton = !isCloseButtonHidden;
  const zIndex = useZIndex("modal");
  const contextValue = useMemo(() => {
    return {
      hideCloseIcon: isCloseButtonHidden,
      setHasSidebar
    };
  }, [isCloseButtonHidden]);
  const [showChildren, setShowChildren] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setShowChildren(true);
    }
  }, [isOpen]);
  return /* @__PURE__ */ jsxs(ModalContext.Provider, {
    value: contextValue,
    children: [/* @__PURE__ */ jsxs(ModalLib, {
      open: isOpen,
      onClose,
      classNames: modalClassNames,
      animationDuration: 200,
      showCloseIcon: false,
      closeOnEsc: showCloseButton,
      closeOnOverlayClick: isClosedOnOverlayClick,
      container,
      onAnimationEnd: () => {
        if (!isOpen) {
          setShowChildren(false);
          onCloseAnimationEnd == null ? void 0 : onCloseAnimationEnd();
        }
      },
      ...props,
      children: [isOpen || showChildren ? children : null, showCloseButton && /* @__PURE__ */ jsx(CloseButton, {
        children: /* @__PURE__ */ jsx(Button, {
          variant: "clean",
          icon: /* @__PURE__ */ jsx(IconCancel, {}),
          "aria-label": translate("modal.close"),
          title: translate("modal.close"),
          onClick: onClose
        })
      })]
    }), /* @__PURE__ */ jsx(GlobalStyles, {
      zIndex
    })]
  });
}
ModalBase.Header = Header;
ModalBase.Content = Content;
ModalBase.Footer = Footer;
ModalBase.Sidebar = ModalSidebar;
function Header({
  title,
  secondaryInfo,
  thumbnail,
  backButton,
  additionalActions
}) {
  const {
    hideCloseIcon
  } = useContext(ModalContext);
  return /* @__PURE__ */ jsxs(HeaderStyled, {
    $hideCloseIcon: hideCloseIcon,
    children: [backButton && /* @__PURE__ */ jsx(BackButton, {
      children: /* @__PURE__ */ jsx(Button, {
        variant: "clean",
        icon: /* @__PURE__ */ jsx(IconArrowLeft, {}),
        "aria-label": backButton.title,
        ...backButton
      })
    }), thumbnail && /* @__PURE__ */ jsx("div", {
      children: thumbnail
    }), /* @__PURE__ */ jsxs(Headings, {
      children: [/* @__PURE__ */ jsx(Title, {
        children: title
      }), secondaryInfo && /* @__PURE__ */ jsx(SecondaryInformation, {
        children: secondaryInfo
      })]
    }), additionalActions && /* @__PURE__ */ jsx(AdditionalActions, {
      children: additionalActions
    })]
  });
}
function Content({
  children
}) {
  return /* @__PURE__ */ jsx(ContentStyled, {
    children: /* @__PURE__ */ jsx(ContentInner, {
      children: /* @__PURE__ */ jsx(ContentExpander, {
        children
      })
    })
  });
}
function Footer({
  actionPrimary,
  actionSecondary,
  actionTertiary
}) {
  return /* @__PURE__ */ jsxs(FooterStyled, {
    children: [actionTertiary, /* @__PURE__ */ jsx(ActionsRight, {
      children: /* @__PURE__ */ jsxs(Flex, {
        gap: "4",
        children: [actionSecondary, actionPrimary]
      })
    })]
  });
}
function ModalSidebar({
  children
}) {
  const {
    setHasSidebar
  } = useContext(ModalContext);
  useEffect(() => {
    setHasSidebar(true);
    return () => {
      setHasSidebar(false);
    };
  });
  return /* @__PURE__ */ jsx(SidebarStyled, {
    children: /* @__PURE__ */ jsx(Sidebar, {
      children
    })
  });
}
export {
  ModalBase
};
//# sourceMappingURL=ModalBase.js.map
